/**
 * Auth Service
 * Authentication işlemleri için service
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS, createEndpoint } from '../config/endpoints';
import {
  // Request Types
  LoginRequest,
  RegisterRequest,
  RefreshTokenRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  
  // Response Types
  LoginResponse,
  UserProfile,
  ApiResponse
} from '../types';



import { AUTH_CONFIG } from '../config/api.config';

/**
 * Auth Service Interface
 */
export interface IAuthService {
  // Authentication
  login(credentials: LoginRequest): Promise<LoginResponse>;
  register(userData: RegisterRequest): Promise<ApiResponse<any>>;
  logout(): Promise<void>;
  refreshToken(refreshToken: string): Promise<LoginResponse>;
  
  // Password Operations
  changePassword(data: ChangePasswordRequest): Promise<ApiResponse<void>>;
  forgotPassword(email: string): Promise<ApiResponse<void>>;
  resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<void>>;
  
  // Profile Operations
  getProfile(): Promise<UserProfile>;
  updateProfile(data: Partial<UserProfile>): Promise<UserProfile>;
  
  // Session Management
  validateSession(): Promise<boolean>;
  clearSession(): void;
  
  // Token Management
  getAccessToken(): string | null;
  setAccessToken(token: string): void;
  getRefreshToken(): string | null;
  setRefreshToken(token: string): void;
  
  // User State
  isAuthenticated(): boolean;
  getCurrentUser(): UserProfile | null;
  setCurrentUser(user: UserProfile): void;
  
  // Utility
  verifyEmail(token: string): Promise<ApiResponse<void>>;
  resendVerificationEmail(email: string): Promise<ApiResponse<void>>;
}

/**
 * Auth Service Implementation
 */
export class AuthService implements IAuthService {
  private accessToken: string | null = null;
  private refreshTokenProp: string | null = null;
  private currentUser: UserProfile | null = null;
  private tokenExpiry: number | null = null;

  constructor() {
    this.loadFromStorage();
  }

  // ==================== AUTHENTICATION ====================

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.login(credentials);
      
      if (response.success && response.data) {
        const loginResponse = response.data;
        
        // Token'ları kaydet
        this.setAccessToken(loginResponse.accessToken);
        this.setRefreshToken(loginResponse.refreshToken);
        
        // User bilgisini kaydet
        this.setCurrentUser(loginResponse.user);
        
        // Token expiry'yi hesapla
        this.tokenExpiry = Date.now() + (loginResponse.expiresIn * 1000);
        
        return loginResponse;
      }
      
      throw new Error(response.message || 'Login failed');
      
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<any>> {
    try {
      const endpoint = ENDPOINTS.AUTH.REGISTER;
      const response = await apiClient.customPost<any>(endpoint, userData, {
        requiresAuth: false
      });
      
      return response;
      
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      // API logout çağrısı
      await apiClient.logout();
    } catch (error) {
      console.warn('Logout API call failed, clearing local session:', error);
    } finally {
      // Local session'ı temizle
      this.clearSession();
    }
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    try {
      const request: RefreshTokenRequest = { refreshToken };
      const endpoint = ENDPOINTS.AUTH.REFRESH_TOKEN;
      
      const response = await apiClient.customPost<LoginResponse>(endpoint, request, {
        requiresAuth: false,
        skipAuthRefresh: true // Infinite loop önleme
      });
      
      if (response.success && response.data) {
        const loginResponse = response.data;
        
        // Yeni token'ları kaydet
        this.setAccessToken(loginResponse.accessToken);
        this.setRefreshToken(loginResponse.refreshToken);
        
        // Token expiry'yi güncelle
        this.tokenExpiry = Date.now() + (loginResponse.expiresIn * 1000);
        
        return loginResponse;
      }
      
      throw new Error(response.message || 'Token refresh failed');
      
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearSession(); // Refresh başarısızsa logout
      throw error;
    }
  }

  // ==================== PASSWORD OPERATIONS ====================

  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<void>> {
    try {
      const endpoint = ENDPOINTS.AUTH.CHANGE_PASSWORD;
      return await apiClient.customPost<void>(endpoint, data);
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<ApiResponse<void>> {
    try {
      const request: ForgotPasswordRequest = { email };
      const endpoint = ENDPOINTS.AUTH.FORGOT_PASSWORD;
      
      return await apiClient.customPost<void>(endpoint, request, {
        requiresAuth: false
      });
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<void>> {
    try {
      const endpoint = ENDPOINTS.AUTH.RESET_PASSWORD;
      
      return await apiClient.customPost<void>(endpoint, data, {
        requiresAuth: false
      });
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  // ==================== PROFILE OPERATIONS ====================

  async getProfile(): Promise<UserProfile> {
    try {
      // Önce cache'ten kontrol et
      if (this.currentUser) {
        return this.currentUser;
      }
      
      const endpoint = ENDPOINTS.AUTH.PROFILE;
      const response = await apiClient.customGet<UserProfile>(endpoint, {
        cacheKey: 'user_profile',
        cacheTTL: 5 * 60 * 1000 // 5 dakika
      });
      
      if (response.success && response.data) {
        this.setCurrentUser(response.data);
        return response.data;
      }
      
      throw new Error(response.message || 'Failed to get profile');
      
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  async updateProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const endpoint = ENDPOINTS.AUTH.PROFILE;
      const response = await apiClient.customPut<UserProfile>(endpoint, data);
      
      if (response.success && response.data) {
        // Mevcut user bilgisiyle merge et
        const updatedUser = {
          ...this.currentUser,
          ...response.data
        } as UserProfile;
        
        this.setCurrentUser(updatedUser);
        return updatedUser;
      }
      
      throw new Error(response.message || 'Failed to update profile');
      
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  // ==================== SESSION MANAGEMENT ====================

  async validateSession(): Promise<boolean> {
    try {
      // Token yoksa false
      if (!this.accessToken) {
        return false;
      }
      
      // Token expiry kontrolü
      if (this.tokenExpiry && Date.now() >= this.tokenExpiry) {
        // Token expired, refresh etmeyi dene
        if (this.refreshTokenProp) {
          try {
            await this.refreshToken(this.refreshTokenProp);
            return true;
          } catch {
            return false;
          }
        }
        return false;
      }
      
      // Profil bilgisini getir (session validasyonu için)
      await this.getProfile();
      return true;
      
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  }

  clearSession(): void {
    // Memory'deki state'i temizle
    this.accessToken = null;
    this.refreshTokenProp = null;
    this.currentUser = null;
    this.tokenExpiry = null;
    
    // Storage'dan temizle
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.USER_DATA);
    
    // Session storage'ı temizle
    sessionStorage.clear();
    
    // API client'ın auth state'ini temizle
    apiClient.clearAuth();
    
    // Auth change event'i gönder
    this.emitAuthChange(false);
  }

  // ==================== TOKEN MANAGEMENT ====================

  getAccessToken(): string | null {
    return this.accessToken;
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
    localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN, token);
    apiClient.setAuthToken(token);
  }

  getRefreshToken(): string | null {
    return this.refreshTokenProp;
  }

  setRefreshToken(token: string): void {
    this.refreshTokenProp = token;
    localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  // ==================== USER STATE ====================

  isAuthenticated(): boolean {
    return !!this.accessToken && !!this.currentUser;
  }

  getCurrentUser(): UserProfile | null {
    return this.currentUser;
  }

  setCurrentUser(user: UserProfile): void {
    this.currentUser = user;
    localStorage.setItem(
      AUTH_CONFIG.STORAGE_KEYS.USER_DATA,
      JSON.stringify(user)
    );
    
    // User change event'i gönder
    this.emitUserChange(user);
  }

  // ==================== UTILITY METHODS ====================

  async verifyEmail(token: string): Promise<ApiResponse<void>> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.AUTH.VERIFY_EMAIL);
      return await apiClient.customPost<void>(endpoint, { token }, {
        requiresAuth: false
      });
    } catch (error) {
      console.error('Verify email error:', error);
      throw error;
    }
  }

  async resendVerificationEmail(email: string): Promise<ApiResponse<void>> {
    try {
      const endpoint = ENDPOINTS.AUTH.VERIFY_EMAIL;
      return await apiClient.customPost<void>(endpoint, { email }, {
        requiresAuth: false
      });
    } catch (error) {
      console.error('Resend verification email error:', error);
      throw error;
    }
  }

  // ==================== PRIVATE METHODS ====================

  private loadFromStorage(): void {
    try {
      // Access token
      const storedAccessToken = localStorage.getItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
      if (storedAccessToken) {
        this.accessToken = storedAccessToken;
        apiClient.setAuthToken(storedAccessToken);
      }
      
      // Refresh token
      const storedRefreshToken = localStorage.getItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
      if (storedRefreshToken) {
        this.refreshTokenProp = storedRefreshToken;
      }
      
      // User data
      const storedUserData = localStorage.getItem(AUTH_CONFIG.STORAGE_KEYS.USER_DATA);
      if (storedUserData) {
        try {
          this.currentUser = JSON.parse(storedUserData);
        } catch (parseError) {
          console.error('Failed to parse user data:', parseError);
          localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.USER_DATA);
        }
      }
      
    } catch (error) {
      console.error('Failed to load auth state from storage:', error);
      this.clearSession();
    }
  }

  private emitAuthChange(isAuthenticated: boolean): void {
    const event = new CustomEvent('auth-state-changed', {
      detail: { isAuthenticated }
    });
    globalThis.dispatchEvent(event);
  }

  private emitUserChange(user: UserProfile | null): void {
    const event = new CustomEvent('user-changed', {
      detail: { user }
    });
    globalThis.dispatchEvent(event);
  }

  // ==================== EVENT LISTENERS ====================

  /**
   * Auth state değişikliklerini dinlemek için
   */
  onAuthChange(callback: (isAuthenticated: boolean) => void): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      callback(customEvent.detail.isAuthenticated);
    };
    
    globalThis.addEventListener('auth-state-changed', handler);
    
    // Cleanup function
    return () => {
      globalThis.removeEventListener('auth-state-changed', handler);
    };
  }

  /**
   * User değişikliklerini dinlemek için
   */
  onUserChange(callback: (user: UserProfile | null) => void): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      callback(customEvent.detail.user);
    };
    
    globalThis.addEventListener('user-changed', handler);
    
    // Cleanup function
    return () => {
      globalThis.removeEventListener('user-changed', handler);
    };
  }

  /**
   * Session timeout'u dinlemek için
   */
  onSessionTimeout(callback: () => void): () => void {
    const handler = () => {
      callback();
    };
    
    globalThis.addEventListener('session-timeout', handler);
    
    // Cleanup function
    return () => {
      globalThis.removeEventListener('session-timeout', handler);
    };
  }

  /**
   * Token refresh edildiğinde dinlemek için
   */
  onTokenRefreshed(callback: () => void): () => void {
    const handler = () => {
      callback();
    };
    
    globalThis.addEventListener('token-refreshed', handler);
    
    // Cleanup function
    return () => {
      globalThis.removeEventListener('token-refreshed', handler);
    };
  }
}

// Singleton instance
export const authService = new AuthService();

// Default export
export default authService;